/* eslint-disable */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Actions
import {
  setMap,
  setGeolocation,
  setZoom,
  setSelectedLocation,
  updateBounds,
  retrieveLocations,
  retrieveLocationDetails,
  setServiceAvailable,
  setServiceCountry,
} from '../actions';

// Components
import GoogleApiWrapper from './GoogleApiWrapper';
import Map from '../components/Map/Map';
import InfoWindow from '../components/InfoWindow/InfoWindow';

// Helpers
import { MIN_ZOOM, DEFAULT_ZOOM, DEFAULT_ZOOM_LEVELS, MOBILE } from '../constants';
import MapUtils from '../lib/MapUtils/MapUtils';
import MarkerUtils from '../lib/MapUtils/MarkerUtils';
import PopupUtils from '../lib/MapUtils/PopupUtils';
import { getDistanceFromLatLonInKm } from '../lib/DistanceUtils';
import { buildTripPlannerLink } from '../lib/UrlUtils';
import { getCenterFromBounds } from '../lib/LocationUtils';
import { pushLocationInteraction, pushTripPlannerInteraction } from '../lib/AnalyticsUtils';

// Data
import mapColor from '../data/mapColor.json';
import serviceCountries from '../data/serviceCountries';

/**
 * @description GoogleMap container handles most logic for Google Maps DirectionService & waypoints
 *
 * @param {object} props - Component props
 * @param {boolean} props.loaded - Lets the application know the Google API & Map has loaded
 * @param {object} props.google - Google Map API passed as props from higher order component
 * @param {function} props.dispatchSetGoogleMapGeolocation - If geolocation was used
 *
 * @returns {<Map />}
 */
class MapContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tripPlannerMarker: null,
      showInfoWindow: null,
      infoWindowCoords: {},
      updatedLocations: null,
    };

    // Bind event handlers
    this.loadMap = this.loadMap.bind(this);
    this.initMap = this.initMap.bind(this);
    this.getGeolocationFromBrowser = this.getGeolocationFromBrowser.bind(this);
    this.handleLocationSelection = this.handleLocationSelection.bind(this);
    this.handleTripPlannerHandoff = this.handleTripPlannerHandoff.bind(this);
    this.renderTripPlannerMarker = this.renderTripPlannerMarker.bind(this);
    this.toggleInfoWindow = this.toggleInfoWindow.bind(this);
    this.getClosestTeslaLocation = this.getClosestTeslaLocation.bind(this);
    this.getLocationsDistances = this.getLocationsDistances.bind(this);

    this.node = {};
    this.markers = {};
  }

  componentDidMount() {
    const { bounds } = this.props.map;
    if (bounds) this.props.retrieveLocations(bounds);
  }

  componentDidUpdate(prevProps) {
    const { searchedLocation, bounds, zoom } = this.props;
    const { map } = this.props.map;
    const { tripPlannerMarker, showInfoWindow } = this.state;

    // Render markers
    const { selected: newSelected, locations: newLocations } = this.props.locations;
    const { selected: prevSelected, locations: prevLocations } = prevProps.locations;

    // Only re-render markers upon new locations, new selected location, or new zoom.
    if (newLocations !== prevLocations || newSelected !== prevSelected || prevProps.zoom !== zoom) {
      this.renderMarkers(newLocations, newSelected);

      this.setState({
        updatedLocations: newLocations,
      });
    }

    // viewport orientation change.
    if (showInfoWindow && bounds !== prevProps.bounds) {
      const infoWindowPosition = PopupUtils.handleInfoWindow(tripPlannerMarker, map, false);

      this.setState({
        infoWindowCoords: infoWindowPosition,
      });
    }

    // hide trip planner lead gen window on excessive zoom out level.
    if (showInfoWindow && zoom !== prevProps.zoom) {
      if (zoom < DEFAULT_ZOOM) {
        this.setState({ showInfoWindow: false });
      }
    }

    // Render trip planner marker.
    if (searchedLocation !== null && searchedLocation !== prevProps.searchedLocation) {
      this.props.setSelectedLocation(null);
      this.renderTripPlannerMarker();
    }

    // If the selected location is changed, center to that new location.
    //
    // On mobile, when we unselect a location, then center the map on this location for usability
    // purposes.
    if (newSelected && newSelected !== prevSelected) {
      this.centerMap(newSelected);
    } else if (!newSelected && prevSelected && window.outerWidth < MOBILE) {
      this.centerMap(prevSelected);
    }
  }

  /**
   * Gets the map center for the specified selected location and bounds.
   *
   * On desktop, this will simply be the lat and lng of the location.
   *
   * On mobile, since the location details card shrinks the viewable map area, we need to calculate
   * where to plot the point so that it appears in the center of the available map area.
   *
   * @param {Object} location - Selected location.
   * @param {Object} bounds - Current map bounds.
   * @return {Object} - Lat and Lng of the map center.
   */
  getMapCenterForLocation(location, bounds) {
    const { lng } = location;

    let { lat } = location;

    if (this.isReducedView()) {
      const { northeast: ne, southwest: sw } = bounds;
      const totalLat = ne.lat - sw.lat;

      const centerOffset = 0.325;
      const headerOffset = 32;

      // The card covers 65% of the vertical viewport. We therefore need push up the center 32.5%
      // from the selected location's lat to center the selected location in the remaining 35%
      // viewport.
      //
      // 32.5% = 50% - 35% / 2
      //
      // We then offset by 32 pixels (calculated by using the ratio of total latitude to total
      // viewport height). This accounts for both the header of 52px as well as offsets some of the
      // marker icon's height.
      lat = lat - centerOffset * totalLat + (headerOffset * totalLat) / window.innerHeight;
    }

    return { lat, lng };
  }

  getGeolocationFromBrowser(map) {
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          if (this.props.map.geolocation) return;
          const { latitude: lat, longitude: lng } = position.coords;
          const { google } = this.props;

          MapUtils.centerMap(lat, lng, map, google);
          this.props.setGeolocation(true);
          MapUtils.setZoom(DEFAULT_ZOOM, map);
        },
        () => { },
        { maximumAge: 3600000 }
      );
    }
  }

  centerMap(selected) {
    // Center map if the selected location is changed.
    const { google, map } = this.props;
    const { lat: targetLat, lng: targetLng } = this.getMapCenterForLocation(selected, map.bounds);

    MapUtils.centerMap(targetLat, targetLng, map.map, google);
  }

  isReducedView() {
    return this.props.locations.selected && window.outerWidth < MOBILE;
  }

  toggleInfoWindow() {
    const { showInfoWindow, tripPlannerMarker } = this.state;
    const { map } = this.props.map;

    // Update window position before we toggle the window show it.
    if (!showInfoWindow) {
      const infoWindowPosition = PopupUtils.handleInfoWindow(tripPlannerMarker, map, true);
      this.setState({
        infoWindowCoords: infoWindowPosition,
      });
    }

    // Toggle Show/Hide window states.
    this.setState({ showInfoWindow: !showInfoWindow });
  }

  /**
   * Retrieves location details for the selected location.
   *
   * @param {Object} location - Selected location.
   * @param {String} type - Location type.
   */
  handleLocationSelection(location, type) {
    const { tripPlannerMarker, showInfoWindow } = this.state;

    // Hide trip Planner Window when clicking on any other location markers.
    if (tripPlannerMarker) {
      if (showInfoWindow === true) {
        this.setState({ showInfoWindow: false });
      }
    }

    this.props.retrieveLocationDetails(location.id);
    pushLocationInteraction({ type, id: location.id });
  }

  handleTripPlannerHandoff() {
    const { searchedLocation } = this.props;
    const tripPlannerUpdatedLink = buildTripPlannerLink('/trips', searchedLocation);
    if (tripPlannerUpdatedLink) {
      window.open(tripPlannerUpdatedLink, '_blank');
      pushTripPlannerInteraction();
    }
  }

  bindBoundsChangedHandler(map) {
    const { google } = this.props;
    const changeHandler = this.handleBoundsChanged.call(this, map);
    google.maps.event.addListener(map, 'bounds_changed', changeHandler);

    map.addListener('click', e => {
      const { tripPlannerMarker, showInfoWindow } = this.state;
      if (tripPlannerMarker) {
        if (
          e.latLng.lat() !== tripPlannerMarker.position.lat() &&
          e.latLng.lng() !== tripPlannerMarker.position.lng()
        ) {
          if (showInfoWindow === true) {
            this.setState({ showInfoWindow: false }); // TODO: move to change handler functions.
          }
        }
      }
    });

    map.addListener('drag', () => {
      const { showInfoWindow } = this.state;

      if (showInfoWindow === true) {
        this.setState({ showInfoWindow: false }); // TODO: move to change handler functions.
      }
    });
  }

  bindWindowResizeHandler(map) {
    const { google } = this.props;
    google.maps.event.addDomListener(window, 'resize', () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      google.maps.event.trigger(map, 'resize');

      // Hack to make the markers re-render.
      map.setZoom(zoom + 1);
      map.setZoom(zoom);

      // Set center.
      map.setCenter(center);
    });
  }

  /**
   * Debounces updating the bounds for the map.
   *
   * @param {Object} map - Google maps object.
   * @param {Number} timer - Debounce timer.
   */
  handleBoundsChanged(map, timer = 50) {
    let timeout = null;
    const updateServiceAvailability = this.updateServiceAvailability.call(this, map);

    return () => {
      clearTimeout(timeout);

      MapUtils.checkBounds(map);
      // Update service availability as the map bounds change.
      updateServiceAvailability();

      timeout = setTimeout(async () => {
        const bounds = MapUtils.convertBounds(map.getBounds());
        const zoom = map.getZoom();

        this.props.updateBounds(bounds);
        this.props.setZoom(zoom);
        this.props.google.maps.event.trigger(map, 'resize');
      }, timer);
    };
  }

  /**
   * Update the service availability depending on the center position of the map. This is debounced
   * to limit the number of Google api requests made.
   *
   * @param {Object} map - Google maps object.
   * @param {Number} timer - Debounce timer.
   */
  updateServiceAvailability(map, timer = 1000) {
    let timeout = null;

    return () => {
      clearTimeout(timeout);

      timeout = setTimeout(async () => {
        // Check if the new map center is in a service available area.
        const country = await MapUtils.getCountry(map);
        this.props.setServiceAvailable(serviceCountries.indexOf(country) >= 0);
        this.props.setServiceCountry(country);
      }, timer);
    };
  }

  loadMap(node) {
    const { google } = this.props;

    if (google) {
      this.node = node;
      this.initMap();
    }
  }

  initMap() {
    const { google } = this.props;
    const { bounds } = this.props.map;
    const { selected, initialLocation } = this.props.locations;

    let center = {
      lat: null,
      lng: null,
    };

    let { zoom } = this.props.map;

    // Default zoom to location if one is selected by default without initial bounds.
    const zoomToLocation = initialLocation && selected;

    if (zoomToLocation) {
      const { lat, lng } = selected;

      center = { lat, lng };
      zoom = DEFAULT_ZOOM;
    } else if (bounds) {
      center = getCenterFromBounds(bounds);
    }

    // Instantiate map
    const mapConfig = {
      center: new google.maps.LatLng(center.lat, center.lng),
      minZoom: MIN_ZOOM,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      zoom,
      zoomControlOptions: {
        style: google.maps.ZoomControlStyle.DEFAULT,
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      streetViewControl: false,
      mapTypeControl: false,
      panControl: false,
      styles: mapColor,
      fullscreenControl: false,
      gestureHandling: 'greedy',
    };

    const map = new google.maps.Map(this.node, mapConfig);

    // Set initial bounds if defined.
    if (bounds && !zoomToLocation) {
      map.fitBounds(new google.maps.LatLngBounds(bounds.southwest, bounds.northeast), 0);
    }

    // allow for custom marker windows
    PopupUtils.initOverlayView(map);

    // Let the application know the map has been instantiated
    this.props.setMap(map);

    // Attempt to get users geolocation from browser.
    if (!this.props.kiosk) {
      this.getGeolocationFromBrowser(map);
    }

    this.bindBoundsChangedHandler(map); // TODO rename to more general bind google map custom even handler?
    this.bindWindowResizeHandler(map);
  }

  getClosestTeslaLocation(locations, distance) {
    return locations.reduce(
      (closestLocation, location) =>
        location[distance] < closestLocation[distance] ? location : closestLocation,
      locations[0]
    );
  }

  renderTripPlannerMarker() {
    const { map } = this.props.map;
    const { searchedLocation } = this.props;
    const { tripPlannerMarker } = this.state;
    let showInfoWindow = false;
    let infoWindowPosition = null;

    // Clear any previous marker.
    if (tripPlannerMarker) {
      tripPlannerMarker.setMap(null);
    }

    // Create new marker based on user input searched location.
    const tripPlannerMarkerDetails = {
      types: ['default'],
      lat: searchedLocation.lat,
      lng: searchedLocation.lng,
      locationType: searchedLocation.type,
    };

    const newMarker = MarkerUtils.createMarker(tripPlannerMarkerDetails, map);

    setTimeout(() => {
      // Determine Whether there is a Tesla location within a 1.6 km / 1 mile radius from our user searched location.
      // This is an approximation the Lat and Lng for a given street address are not always matching.
      // Tesla locations lat/lng are pulled from our private API. User input searched location uses GoogleMap API.
      const updatedTslLocations = this.getLocationsDistances(
        newMarker,
        this.state.updatedLocations
      );
      const closestTslDestination = this.getClosestTeslaLocation(
        updatedTslLocations,
        'distanceToUserInputLocation'
      );

      if (searchedLocation.isFullStreetAddress) {
        if (closestTslDestination && closestTslDestination.distanceToUserInputLocation < 1.6) {
          // Retrieves and display Tesla location details.
          this.props.retrieveLocationDetails(closestTslDestination.id);
        } else {
          // handles opening of trip planner lead gen window
          PopupUtils.handleInfoWindow(newMarker, map);
          infoWindowPosition = PopupUtils.handleInfoWindow(newMarker, map);
          showInfoWindow = true;
          newMarker.addListener('click', () => {
            this.toggleInfoWindow();
          });
        }
      }

      this.setState({
        showInfoWindow,
        infoWindowCoords: infoWindowPosition,
        tripPlannerMarker: newMarker,
      });
    }, 300);

    map.panTo(newMarker.getPosition());

    const zoomLevel = DEFAULT_ZOOM_LEVELS[tripPlannerMarkerDetails.locationType] || DEFAULT_ZOOM;
    MapUtils.setZoom(zoomLevel, map);

    return null;
  }

  /**
   * given a base location point will return a new updated Array
   * with the new distanceToUserInputLocation field containing the distance in km between the
   * base location and the targeted location.
   */
  getLocationsDistances(base, locations) {
    const baseLat = base.position.lat();
    const baseLng = base.position.lng();

    return locations.map(location => {
      location.distanceToUserInputLocation = getDistanceFromLatLonInKm(
        baseLat,
        baseLng,
        location.lat,
        location.lng
      );
      return location;
    });
  }

  /**
   * Render map markers for the provided locations. When locations change, identifies the removed
   * or added locations and only adds / remove those markers. Existing markers remain on the map
   * and are update if their current icon type is no longer available.
   *
   * The currently rendered markers are stored in the "markers" property with the following format:
   * {
   *  MARKER_ID: {
   *    marker,
   *    location,
   *  }
   * }
   *
   * where MARKER_ID is the id of the location, marker is a reference to the google map marker,
   * and location holds the location data.
   *
   * The selected location is passed to update the marker icon for the selected marker.
   *
   * @param {Array<Object>} locations - Location data to be rendered as markers.
   */
  async renderMarkers(locations) {
    const { map, zoom } = this.props.map;

    if (map) {
      let updatedMarkers = { ...this.markers };

      const locationsToAdd = [];
      const locationsToRemove = { ...updatedMarkers };

      // Identify locations to be added and removed.
      locations.forEach(location => {
        if (!updatedMarkers[location.id]) {
          locationsToAdd.push(location);
        }

        delete locationsToRemove[location.id];
      });

      // Remove unused markers from map and the markers list.
      MarkerUtils.clearMarkers(Object.values(locationsToRemove));
      Object.keys(locationsToRemove).forEach(location => delete updatedMarkers[location]);
      // Create new markers.
      const { filters, selected } = this.props.locations;
      const { google } = this.props;
      const click = (location, type) => this.handleLocationSelection(location, type);

      const newMarkers = MarkerUtils.createMarkers(
        locationsToAdd,
        map,
        filters,
        zoom,
        selected,
        click,
        google
      );

      // Update existing markers.
      updatedMarkers = MarkerUtils.updateMarkers(updatedMarkers, filters, zoom, selected, google);

      // Add new markers.
      this.markers = {
        ...updatedMarkers,
        ...newMarkers,
      };
    }
  }

  render() {
    const { showInfoWindow, infoWindowCoords } = this.state;

    return (
      <div className="map_container">
        <Map loadMap={this.loadMap} {...this.props} />
        {showInfoWindow && (
          <InfoWindow
            text={this.props.content.trips.label}
            buttonLabel={this.props.content.trips.button.label}
            handleButtonClick={this.handleTripPlannerHandoff}
            position={{ x: infoWindowCoords.x, y: infoWindowCoords.y }}
            dimension={{
              markerHeight: 10,
              offset: 13,
              width: 290,
              height: 146,
            }}
          />
        )}
      </div>
    );
  }
}
MapContainer.defaultProps = {
  content: {},
  locations: {},
};

MapContainer.propTypes = {
  setMap: PropTypes.func.isRequired,
  setGeolocation: PropTypes.func.isRequired,
  setZoom: PropTypes.func.isRequired,
  updateBounds: PropTypes.func.isRequired,
  retrieveLocations: PropTypes.func.isRequired,
  retrieveLocationDetails: PropTypes.func.isRequired,
  google: PropTypes.shape({
    maps: PropTypes.object,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  map: PropTypes.object.isRequired,
  locations: PropTypes.object,
  content: PropTypes.object,
  searchedLocation: PropTypes.object,
  bounds: PropTypes.object.isRequired,
  zoom: PropTypes.number.isRequired,
};

MapContainer.defaultProps = {
  google: null,
  searchedLocation: null,
};

const mapStateToProps = state => {
  return {
    map: state.map,
    app: state.app,
    content: state.content,
    locations: state.locations,
    bounds: state.map.bounds,
    zoom: state.map.zoom,
    searchedLocation: state.search.selectedSearchResult,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setMap: map => dispatch(setMap(map)),
    setGeolocation: geolocation => dispatch(setGeolocation(geolocation)),
    setZoom: zoom => dispatch(setZoom(zoom)),
    updateBounds: bounds => dispatch(updateBounds(bounds)),
    retrieveLocations: bounds => dispatch(retrieveLocations(bounds)),
    retrieveLocationDetails: id => dispatch(retrieveLocationDetails(id)),
    setSelectedLocation: location => dispatch(setSelectedLocation(location)),
    setServiceAvailable: available => dispatch(setServiceAvailable(available)),
    setServiceCountry: country => dispatch(setServiceCountry(country)),
  };
};

// CUA-1560: Show South Korean specific map on ko_KR locale
const MapWrapper = GoogleApiWrapper({
  version: '3.30',
  libraries: ['places'],
  region: window.tesla && window.tesla.App && window.tesla.App.locale === 'ko_KR' ? 'KR' : 'US',
})(MapContainer);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MapWrapper);
